import { User } from "../../../features/user";
import { hasEntitlement } from "../../../utils/has-entitlement";
import { getImpersonation } from "../../../api/impersonation-state";
import {
  localStorageFeatureToggle,
  FeatureName,
} from "../../../utils/feature-toggle-utils";

// Single source of truth for whether the Integrations surface is visible: the
// `external-integrations` Chargebee entitlement, mirrored server-side in
// featureGate.ts. Internal users can preview without it via the localStorage dev
// override. Do not add an Amplitude rollout term here: SKE-5154 hid Integrations
// from every customer because the production flag never assigned its variant.
export function hasExternalIntegrationsAccess(
  user: User | null | undefined
): boolean {
  return (
    hasEntitlement(user, "external-integrations") ||
    localStorageFeatureToggle.get(FeatureName.ExternalIntegrations)
  );
}

export function useExternalIntegrationsAccess(
  user: User | null | undefined
): boolean {
  return hasExternalIntegrationsAccess(user);
}

// True when the org can SEE integrations but holds no tier — the legacy-plan case
// (SKE-4405). Every integration then renders "Upgrade" (the server decides that,
// per provider) and the page carries an explanatory banner, so the user knows why
// nothing is connectable rather than assuming it's broken.
//
// This is the ONLY tier logic on the client: which integrations a tier reaches is
// resolved server-side and arrives as `requiresUpgrade` per provider, so packaging
// can change without a client release. Here we only need "tier absent entirely".
//
// Null-tolerant: `hasExternalIntegrationsAccess` can return true from the localStorage
// override with no user loaded yet, so this must not assume `user` is present.
export function isLegacyIntegrationsPlan(
  user: User | null | undefined
): boolean {
  if (!hasExternalIntegrationsAccess(user)) {
    return false;
  }
  return !user?.preferences?.entitlements?.["external-integration-tiers"];
}

// Whether the user may CONFIGURE integrations (connect / reconnect / disconnect /
// save config), as opposed to just VIEW them (SKE-3956). Visibility stays
// entitlement-based via hasExternalIntegrationsAccess; this is the separate
// role/permission check. Admins/owners always may; a collaborator may only when an
// admin has turned on `canConfigureIntegrations` (default off). Mirrors the server
// gate `requireIntegrationsManageAccess`.
export function canManageIntegrations(user: User): boolean {
  // Support access is view-only: the server refuses every integrations change
  // during impersonation (SKE-5103).
  if (getImpersonation()) {
    return false;
  }
  return customerCanManageIntegrations(user);
}

export function customerCanManageIntegrations(user: User): boolean {
  if (user.role === "admin") {
    return true;
  }
  return user.preferences?.canConfigureIntegrations === true;
}
